import { isRedirect, notFound, redirect } from "@tanstack/react-router";
import { fetchCurrentUser } from "@/lib/auth";
import { apiClient } from "@/lib/apiClient";
import { queryKeys } from "@/lib/query";
import type { QueryClient } from "@tanstack/react-query";
import { OrganizationType, UserType } from "@/types";
import type { UserRoleValue, UserTypeValue } from "@/types";
import type { FinancierItem } from "@/types/api/financier";
import type { MerchantItem } from "@/types/api/merchant";
import type { CurrentOrganization, CurrentUser } from "@/types/api/user";
import { isWalletSetupNavigationLocked } from "@/lib/wallet/walletSetupLock";
import { hasEntityPublicKey } from "@/utils/helpers";
import { resolvePostLoginPath } from "@/routing/safeInternalPath";

export const WALLET_SETUP_PATH = "/user/wallet-setup";

/** Routes reachable before a customer org has a wallet. */
export const WALLET_SETUP_ALLOWED_PATHS = [
  WALLET_SETUP_PATH,
  "/user/settings",
  "/user/manageusers",
] as const;

export type RouterContext = {
  queryClient: QueryClient;
};

type GuardLocation = {
  context: RouterContext;
  cause: "preload" | "enter" | "stay";
  location: {
    href: string;
    pathname: string;
    searchStr: string;
  };
};

function shouldFreshFetch(cause: GuardLocation["cause"]): boolean {
  return cause === "enter";
}

function loginRedirect(location: GuardLocation["location"]) {
  if (location.pathname === WALLET_SETUP_PATH) {
    return redirect({ to: "/login", search: {} });
  }

  return redirect({
    to: "/login",
    search: { redirect: location.href },
  });
}

export function dashboardPathFor(user: CurrentUser): "/admin/dashboard" | "/user/dashboard" {
  return user.userType === UserType.Developer ? "/admin/dashboard" : "/user/dashboard";
}

/** Redirect to /login if unauthenticated, preserving the current URL for post-login return. */
export async function requireAuthenticated({
  context,
  cause,
  location,
}: GuardLocation): Promise<{ user: CurrentUser }> {
  const user = await fetchCurrentUser(context.queryClient, {
    fresh: shouldFreshFetch(cause),
  });

  if (!user) {
    throw loginRedirect(location);
  }

  return { user };
}

/** Redirect to /login if unauthenticated; notFound() if role/type mismatch. */
export function createAccessGuard(
  roles: UserRoleValue[],
  usertypes: UserTypeValue[],
) {
  return async ({ context, cause, location }: GuardLocation) => {
    const user = await fetchCurrentUser(context.queryClient, {
      fresh: shouldFreshFetch(cause),
    });

    if (!user) {
      throw loginRedirect(location);
    }

    if (
      !roles.includes(user.userRole) ||
      !usertypes.includes(user.userType)
    ) {
      throw notFound();
    }

    return { user };
  };
}

function isWalletSetupAllowed(pathname: string): boolean {
  return (WALLET_SETUP_ALLOWED_PATHS as readonly string[]).includes(pathname);
}

async function fetchCurrentOrganization(
  queryClient: QueryClient,
): Promise<CurrentOrganization | null> {
  try {
    return await queryClient.fetchQuery({
      queryKey: queryKeys.auth.organization(),
      queryFn: () => apiClient.get<CurrentOrganization>("/auth/organization"),
      staleTime: 5 * 60 * 1000,
    });
  } catch {
    return null;
  }
}

async function fetchEntityPublicKey(
  queryClient: QueryClient,
  organization: CurrentOrganization,
): Promise<string | null | undefined> {
  const entityId = organization.externalEntityId;
  if (!entityId) return null;

  try {
    if (organization.externalEntityType === OrganizationType.Merchant) {
      const merchant = await queryClient.fetchQuery({
        queryKey: queryKeys.merchants.detail(entityId),
        queryFn: () => apiClient.getData<MerchantItem>(`/merchants/${entityId}`),
      });
      return merchant.publicKey;
    }

    if (organization.externalEntityType === OrganizationType.Financier) {
      const financier = await queryClient.fetchQuery({
        queryKey: queryKeys.financiers.detail(entityId),
        queryFn: () => apiClient.getData<FinancierItem>(`/financiers/${entityId}`),
      });
      return financier.publicKey;
    }
  } catch {
    return undefined;
  }

  return undefined;
}

/**
 * Customer orgs without a wallet are sent to `/user/wallet-setup`.
 * Settings / manage-users stay reachable so the user can log out or finish profile work.
 * Fail open if org/entity cannot be loaded — do not trap them on a broken fetch.
 */
export async function requireWalletSetup({
  context,
  location,
  user,
}: {
  context: RouterContext;
  location: { pathname: string };
  user: CurrentUser;
}): Promise<void> {
  if (user.userType === UserType.Developer) return;

  const locked = isWalletSetupNavigationLocked();
  if (locked && location.pathname !== WALLET_SETUP_PATH) {
    throw redirect({ to: WALLET_SETUP_PATH });
  }

  if (location.pathname === WALLET_SETUP_PATH) {
    if (locked) return;

    const organization = await fetchCurrentOrganization(context.queryClient);
    if (!organization) return;

    const publicKey = await fetchEntityPublicKey(context.queryClient, organization);
    if (publicKey === undefined) return;
    if (hasEntityPublicKey(publicKey)) {
      throw redirect({ to: dashboardPathFor(user) });
    }
    return;
  }

  if (isWalletSetupAllowed(location.pathname)) return;

  const organization = await fetchCurrentOrganization(context.queryClient);
  if (!organization) return;

  const isCustomerOrg =
    organization.externalEntityType === OrganizationType.Merchant
    || organization.externalEntityType === OrganizationType.Financier;
  if (!isCustomerOrg) return;

  const publicKey = await fetchEntityPublicKey(context.queryClient, organization);
  if (publicKey === undefined) return;
  if (hasEntityPublicKey(publicKey)) return;

  throw redirect({ to: WALLET_SETUP_PATH });
}

/** Redirect authenticated users to their dashboard (or a safe `redirect` search param). */
export async function redirectIfAuthenticated({
  context,
  cause,
  location,
}: GuardLocation): Promise<void> {
  try {
    const user = await fetchCurrentUser(context.queryClient, {
      fresh: shouldFreshFetch(cause),
    });

    if (user) {
      const params = new URLSearchParams(location.searchStr);
      const redirectTo = resolvePostLoginPath(params.get("redirect"));

      if (redirectTo) {
        throw redirect({ href: redirectTo });
      }

      throw redirect({ to: dashboardPathFor(user) });
    }
  } catch (error) {
    if (isRedirect(error)) throw error;
    // Unreachable API: still show guest pages (e.g. /login).
  }
}
